/* DISTRIBUSI HP — KIT: the shared chrome of the office Distribusi screens at phone width (≤767px), in
   Mode Lapangan's Liquid Glass (spec docs/superpowers/specs/2026-10-27-distribusi-hp-liquid-glass-design.md,
   mockup …-mockup.html). Glass only on the functional layer (round buttons, the title bar, the floating
   search, the dock + its menu, sheets); content is opaque white cards. Tokens + Mode Lapangan components
   are reused (dist-field.css, dist-field-kit.jsx, dist-field-icons.jsx), never copied. Bundle scope: every
   top-level name here starts with Dhp / dhp / DHP / trDh / uXdh. */
const { useState: uSdh, useEffect: uEdh, useRef: uRdh, useMemo: uMdh } = React;
const trDh = (k, v) => window.t(k, v);

// Dates in the app's locale through one cached formatter per option set: toLocaleDateString builds a new
// formatter per call, which was a large part of rendering a long list.
const dhpDtf = {};
function dhpDateFmt(dt, opts) {
  const loc = trDh('fld.locale'); const k = loc + JSON.stringify(opts);
  const f = dhpDtf[k] || (dhpDtf[k] = new Intl.DateTimeFormat(loc, opts));
  return f.format(dt);
}

// Phone width, live (rotating / resizing across 767px switches the layout).
function useDhpPhone() {
  const q = '(max-width: 767px)';
  const get = () => !!(window.matchMedia && window.matchMedia(q).matches);
  const [on, setOn] = uSdh(get);
  uEdh(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const f = () => setOn(mq.matches);
    f();
    if (mq.addEventListener) { mq.addEventListener('change', f); return () => mq.removeEventListener('change', f); }
    mq.addListener(f); return () => mq.removeListener(f);
  }, []);
  return on;
}

// The scroll container has moved past the large title → the small centred title shows in the bar.
function useDhpScrolled(ref, px) {
  const lim = px == null ? 44 : px;
  const [s, setS] = uSdh(false);
  uEdh(() => {
    const el = ref && ref.current; if (!el) return undefined;
    // A container just mounted sits at the top: reading scrollTop here would force a layout of the new screen
    // inside the tap. Only a crossing re-renders the screen, not every scroll event.
    let cur = false;
    const f = () => { const v = el.scrollTop > lim; if (v !== cur) { cur = v; setS(v); } };
    setS(false); el.addEventListener('scroll', f, { passive: true });
    return () => el.removeEventListener('scroll', f);
  }, [ref, lim, ref && ref.current]);   // a remounted scroll container (detail → list) gets the listener again
  return s;
}

// On-screen keyboard: how much of the layout viewport it covers, as --dhp-kb on <html>, so the CTA bar and
// a raised search box stay above it (spec §6 Input: "tetap terlihat saat keyboard terbuka").
function useDhpKbInset() {
  uEdh(() => {
    const vv = window.visualViewport;
    const de = document.documentElement;
    if (!vv) return undefined;
    // A write on <html> restyles the whole document, so only an actual change is written (opening a form
    // with no keyboard writes nothing).
    let last = de.style.getPropertyValue('--dhp-kb') || '0px';
    const f = () => {
      const v = HPLOGIC.kbInset({ innerHeight: window.innerHeight, vvHeight: vv.height, vvTop: vv.offsetTop }) + 'px';
      if (v !== last) { last = v; de.style.setProperty('--dhp-kb', v); }
    };
    // the first read waits a frame: reading the viewport inside the tap forces a layout of the new screen
    const raf = requestAnimationFrame(f); vv.addEventListener('resize', f); vv.addEventListener('scroll', f);
    return () => { cancelAnimationFrame(raf); vv.removeEventListener('resize', f); vv.removeEventListener('scroll', f); if (last !== '0px') de.style.setProperty('--dhp-kb', '0px'); };
  }, []);
}

// The latest value of something a memoised list calls back into: the rows keep their render while sheets,
// the title bar and the search box change around them (each open/close used to re-render every row).
function useDhpLatest(v) {
  const r = uRdh(v);
  r.current = v;
  return r;
}

// A long list renders in steps: the first `first` rows, then `step` more whenever the sentinel (put after
// the last row) comes within 800 px of the scroll container's edge. A new `reset` starts from `first` again.
function useDhpWindow(scrollRef, total, reset, first, step) {
  const [vis, setVis] = uSdh(first);
  const sentinel = uRdh(null);
  const seen = uRdh(reset);
  if (seen.current !== reset) { seen.current = reset; if (vis !== first) setVis(first); }
  const more = vis < total;
  uEdh(() => {
    if (!more) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVis(total); return undefined; }
    const el = sentinel.current; if (!el) return undefined;
    // a new observer per step: it reports at once, so a short screen keeps filling until the sentinel is out of reach
    const io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) setVis((n) => n + step); }, { root: scrollRef.current, rootMargin: '800px' });
    io.observe(el); return () => io.disconnect();
  }, [vis, more, reset]);
  return { vis, more, sentinel };
}

// A redesigned screen: its own full-screen scroll container over the shell (whose topbar steps aside).
function DhpRoot({ className, scrollRef, children }) {
  uEdh(() => { document.body.classList.add('dhp-full'); return () => document.body.classList.remove('dhp-full'); }, []);
  return <div ref={scrollRef} className={'dhp-root' + (className ? ' ' + className : '')}>{children}</div>;
}

// The large title that scrolls with the list (boards 1, 2, 4): ☰ left, round buttons right, a sub line.
function DhpHead({ title, onMenu, right, eyebrow }) {
  return (
    <>
      <div className="dhp-head">
        {onMenu ? <DhpRound icon="menu" label={trDh('dhp.menu')} onClick={onMenu} /> : null}
        <h1>{title}</h1>
        {right ? <div className="dhp-head-r">{right}</div> : null}
      </div>
      {eyebrow ? <div className="dhp-eyebrow">{eyebrow}</div> : null}
    </>
  );
}

// The glass title bar of a detail / task screen: back (round ←) or a "Batal" pill, the centred title
// (shown once `show`), round buttons right.
function DhpTop({ title, show, onBack, backKind, backLabel, right }) {
  return (
    <div className="mlap-top dhp-top">
      {onBack ? (backKind === 'pill'
        ? <button type="button" className="mlap-pill" onClick={onBack}>{backLabel || trDh('dhp.cancel')}</button>
        : <button type="button" className="mlap-round" aria-label={trDh('dhp.back')} onClick={onBack}><FldSvg n="left" s={20} sw={2.2} /></button>) : <span aria-hidden="true" />}
      <h1 className={'dhp-top-t' + (show === false ? ' off' : '')}>{title}</h1>
      <div className="dhp-top-r">{right}</div>
    </div>
  );
}

function DhpRound({ icon, label, onClick, dot, className }) {
  return (
    <button type="button" className={'mlap-round dhp-round' + (className ? ' ' + className : '')} aria-label={label} onClick={onClick}>
      <FldSvg n={icon} s={20} sw={1.9} />{dot ? <span className="dhp-dot" aria-hidden="true" /> : null}
    </button>
  );
}

// Active filters as removable chips (board 1).
function DhpChips({ chips, onRemove }) {
  if (!chips || !chips.length) return null;
  return (
    <div className="dhp-chips">
      {chips.map((c) => (
        <button key={c.key} type="button" className="dhp-chip x" onClick={() => onRemove(c.key)} aria-label={trDh('dhp.removeFilter', { f: c.label })}>
          {c.icon ? <FldSvg n={c.icon} s={14} /> : null}<span>{c.label}</span><FldSvg n="close" s={12} sw={2.4} />
        </button>
      ))}
    </div>
  );
}

// Equal-width segments (boards 2, 5): options [[key, label, icon?, ariaLabel?]] (an icon-only segment names itself).
function DhpSeg({ options, value, onChange, label }) {
  return (
    <div className="dhp-seg" role="tablist" aria-label={label}>
      {options.map(([k, t, ic, aria]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} aria-label={aria} className={value === k ? 'on' : ''} onClick={() => onChange(k)}>
          {ic ? <FldSvg n={ic} s={16} /> : null}{t ? <span>{t}</span> : null}
        </button>
      ))}
    </div>
  );
}

// The floating glass search above the dock, with the Filter button (+ count of active filters).
function DhpSearchPill({ value, onChange, placeholder, onFilter, filterCount, autoFocus, inputRef, raised, fbIcon, fbLabel }) {
  return (
    <div className={'mlap-searchpill dhp-searchpill' + (raised ? ' raised' : '')} role="search">
      <FldSvg n="search" s={18} />
      <input ref={inputRef} type="search" value={value} autoFocus={autoFocus} placeholder={placeholder} aria-label={placeholder} onChange={(e) => onChange(e.target.value)} />
      {value ? <button type="button" className="dhp-clear" aria-label={trDh('dhp.clearSearch')} onClick={() => onChange('')}><FldSvg n="close" s={16} sw={2.2} /></button> : null}
      {onFilter ? (
        <button type="button" className="dhp-fb" onClick={onFilter}>
          <FldSvg n={fbIcon || 'filter'} s={16} />{fbLabel || trDh('dhp.filter')}{filterCount ? <b>{filterCount}</b> : null}
        </button>
      ) : null}
    </div>
  );
}

// A glass sheet from the bottom: grabber (pull down to close), title, body, optional CTA row.
function DhpSheet({ title, sub, onClose, tall, children, cta }) {
  const drag = useFldSheetDrag(onClose);
  return (
    <>
      <button type="button" className="mlap-scrim" aria-label={trDh('dhp.close')} onClick={onClose} />
      <div ref={drag.ref} style={drag.style} className={'mlap-sheet dhp-sheet' + (tall ? ' tall' : '')} role="dialog" aria-modal="true" aria-label={title}>
        <FldGrab handle={drag.handle} />
        {title ? <h2 className="dhp-sheet-t">{title}</h2> : null}
        {sub ? <div className="dhp-sheet-sub">{sub}</div> : null}
        <div className="dhp-sheet-body">{children}</div>
        {cta ? <div className="dhp-sheet-cta">{cta}</div> : null}
      </div>
    </>
  );
}

// The ⋯ menu as grouped cards in a sheet (board 5e). groups: [{ labelKey, items: [{ key, labelKey|label, aria?, icon, danger, onClick }] }];
// empty groups (no caps) are not drawn. `sub` is an optional line under the title (board 4b).
function DhpMenuSheet({ title, sub, groups, onClose }) {
  const gs = (groups || []).map((g) => Object.assign({}, g, { items: (g.items || []).filter(Boolean) })).filter((g) => g.items.length);
  return (
    <DhpSheet title={title} sub={sub} onClose={onClose}>
      {gs.map((g, gi) => (
        <div key={g.labelKey || gi}>
          {g.labelKey ? <div className="dhp-lbl">{trDh(g.labelKey)}</div> : null}
          <div className="mlap-card dhp-menu-card">
            {g.items.map((it) => (
              <button key={it.key} type="button" className={'dhp-mi' + (it.danger ? ' neg' : '')} aria-label={it.aria} onClick={() => { onClose(); it.onClick(); }}>
                <span className="dhp-mi-ic"><FldSvg n={it.icon || 'right'} s={17} /></span>
                <span className="dhp-mi-t">{it.label || trDh(it.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </DhpSheet>
  );
}

// The + menu: glass panel morphing out of the centre button, 2×2 tiles (board 3).
function DhpCatatMenu({ tiles, onPick, onClose }) {
  return (
    <>
      <button type="button" className="mlap-scrim menu" aria-label={trDh('dhp.cancel')} onClick={onClose} />
      <div className="mlap-catat-menu dhp-catat-menu" role="menu" aria-label={trDh('dhp.catatTitle')}>
        <div className="dhp-catat-grid">
          {tiles.map((t, i) => (
            <button key={t.kind} type="button" role="menuitem" className="mlap-tile dhp-tile" style={{ animationDelay: (70 + i * 40) + 'ms' }} onClick={() => { onClose(); onPick(t.kind); }}>
              <span className={'dhp-tile-ic ' + t.tone}><FldSvg n={t.icon} s={19} /></span>
              <span className="dhp-tile-t">{trDh(t.labelKey)}</span>
              <small>{trDh(t.subKey)}</small>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// The Distribusi dock (replaces the app's phone bottom nav on dist-* screens): FldDock with the tabs the
// user may open and the + only with an input cap; the + menu offers what the caps allow.
function DhpDock({ navIds, perms, screen, onGo, onPick }) {
  const [open, setOpen] = uSdh(false);
  const lay = uMdh(() => HPLOGIC.dockLayout({ navIds, perms }), [navIds.join(','), perms]);
  const tiles = uMdh(() => HPLOGIC.catatTiles(perms), [perms]);
  if (!lay.tabs.length && !lay.catat) return null;
  // The + sits in the dock's 70px gap. Mode Lapangan centres it on the screen; here a user may lack a tab on
  // one side, so the gap's centre is computed from the same widths the dock uses (FldDock's CSS).
  const n = lay.tabs.filter(Boolean).length; const nL = lay.tabs.indexOf(null);
  const W = 'min(448px, calc(100vw - 32px))';
  const fr = '((' + W + ' - 78px - ' + ((lay.tabs.length - 1) * 2) + 'px) / ' + Math.max(1, n) + ')';
  const cx = nL < 0 ? '50%' : 'calc((100vw - ' + W + ') / 2 + 39px + ' + nL + ' * (' + fr + ' + 2px))';
  return (
    <div className="dhp-tok dhp-dockhost" style={{ '--dhp-cx': cx }}>
      <div className="mlap-dockfade" aria-hidden="true" />
      <FldDock tabs={lay.tabs} tab={screen} onTab={(id) => { setOpen(false); onGo(id); }} labelOf={(id) => trDh('dhp.tab.' + id)}
        catat={open} onCatat={lay.catat ? () => setOpen(!open) : undefined} style={{ gridTemplateColumns: lay.cols }} />
      {open ? <DhpCatatMenu tiles={tiles} onPick={onPick} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
